import { Capacitor } from '@capacitor/core';
import { SocialLogin } from '@capgo/capacitor-social-login';

/**
 * Login nativo do Google para o app empacotado em Capacitor (Android/iOS).
 *
 * Por que existe: o `@react-oauth/google` (usado no PWA) abre o consentimento
 * do Google num webview embarcado, que o Google bloqueia com
 * `disallowed_useragent`. No app nativo precisamos do SDK nativo de Sign-In.
 *
 * Estratégia que mantém o backend intacto: inicializamos o plugin com
 * `webClientId` = o MESMO Google Client ID web que o backend já valida
 * (`BOW_GOOGLE_CLIENT_ID`, servido em runtime via `/auth/config`). Em modo
 * `online` (default) o `login()` devolve um `idToken` cujo `aud` é esse web
 * client ID — exatamente o que `POST /auth/google` espera. Nenhuma mudança no
 * backend, nenhum segundo audience.
 *
 * O OAuth Client ID *Android* (com SHA-1 do upload key e da app signing key)
 * precisa existir no Google Cloud pra o Google liberar o fluxo, mas seu valor
 * NÃO entra no código — só o web client ID é referenciado aqui.
 */

/** True quando rodando dentro do shell nativo (Android/iOS), não no PWA/web. */
export function isNativeApp(): boolean {
  return Capacitor.isNativePlatform();
}

function isIOSNative(): boolean {
  return isNativeApp() && Capacitor.getPlatform() === 'ios';
}

/**
 * OAuth Client ID do tipo *iOS* (bundle `br.app.bow`), criado no mesmo projeto
 * Google Cloud que dona o web client ID (`BOW_GOOGLE_CLIENT_ID`). Diferente do
 * web/Android, o plugin NÃO inicializa o provider Google no iOS sem ele
 * (`SocialLoginPlugin.swift`: o init é gated por `if let clientId = iOSClientId`)
 * — sem isso o `login()` falha de imediato e o botão "não faz nada".
 *
 * Build-time (não é segredo: já vai embarcado no .ipa e como URL scheme no
 * Info.plist). O `codemagic.yaml` deriva o `REVERSED_CLIENT_ID` desse mesmo
 * valor pra registrar a URL scheme do callback — fonte única evita mismatch.
 */
const IOS_CLIENT_ID = import.meta.env.VITE_GOOGLE_IOS_CLIENT_ID ?? '';

/**
 * True quando o botão nativo de Google pode funcionar nesta plataforma. No iOS
 * exige `VITE_GOOGLE_IOS_CLIENT_ID` configurado; sem ele o botão é escondido
 * (degrada pra email/senha + Apple) em vez de virar botão morto.
 */
export function nativeGoogleAvailable(): boolean {
  if (!isNativeApp()) return false;
  if (isIOSNative()) return IOS_CLIENT_ID !== '';
  return true; // Android só precisa do webClientId.
}

let initPromise: Promise<void> | null = null;

/** Inicializa o plugin uma única vez por sessão (idempotente). */
function ensureInit(webClientId: string): Promise<void> {
  if (!initPromise) {
    initPromise = SocialLogin.initialize({
      google: {
        // Mesmo client ID web que o backend valida — ver docstring acima.
        webClientId,
        // iOS: o provider Google só inicializa com `iOSClientId`. E setar
        // `iOSServerClientId` = webClientId faz o `aud` do idToken sair = web
        // client ID (doc oficial do GIDConfiguration.serverClientID), que é o
        // audience estrito que `POST /auth/google` valida. Backend intacto.
        ...(isIOSNative()
          ? { iOSClientId: IOS_CLIENT_ID, iOSServerClientId: webClientId }
          : {})
      }
    }).catch((err) => {
      // Reseta pra permitir nova tentativa num próximo clique.
      initPromise = null;
      throw err;
    });
  }
  return initPromise;
}

/**
 * Dispara o Sign-In nativo do Google e retorna o `idToken` (JWT) pra ser
 * trocado por sessão Bow via `signInWithGoogle`. Lança se o usuário cancelar
 * ou se o Google não devolver token.
 */
export async function nativeGoogleSignIn(webClientId: string): Promise<string> {
  await ensureInit(webClientId);
  const res = await SocialLogin.login({ provider: 'google', options: {} });
  const result = res.result as { idToken?: string | null };
  if (!result?.idToken) {
    throw new Error('google_no_id_token');
  }
  return result.idToken;
}

/**
 * Heurística pra distinguir cancelamento do usuário (silencioso, esperado) de
 * falha real (init/config/rede — merece feedback). O plugin propaga a
 * `localizedDescription` do NSError; o GIDSignIn usa "canceled"/"cancelled".
 */
export function isUserCancellation(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? '');
  return /cancel/i.test(msg);
}
